import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { shareApi } from "@/lib/share";
import AhaCelebrationModal from "@/components/AhaCelebrationModal";

const AhaCelebrationContext = createContext({
  triggerAhaCheck: () => {},
});

function fireConfetti() {
  const colors = ["#E5B83A", "#F5D547", "#FFFFFF", "#FFD700"];
  confetti({
    particleCount: 120,
    spread: 80,
    startVelocity: 55,
    origin: { x: 0.1, y: 1 },
    angle: 60,
    colors,
    zIndex: 70,
    scalar: 1.1,
    ticks: 250,
  });
  confetti({
    particleCount: 120,
    spread: 80,
    startVelocity: 55,
    origin: { x: 0.9, y: 1 },
    angle: 120,
    colors,
    zIndex: 70,
    scalar: 1.1,
    ticks: 250,
  });
}

export function AhaCelebrationProvider({ children }) {
  const [modalOpen, setModalOpen] = useState(false);
  // Concurrency guard — prevents double-fire when Radar + Chat trigger nearly
  // simultaneously. Server is still authoritative; this just avoids two
  // confetti bursts.
  const inFlightRef = useRef(false);

  const triggerAhaCheck = useCallback(async (trigger) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const resp = await shareApi.checkAha(trigger);
      if (resp?.should_celebrate) {
        fireConfetti();
        setTimeout(() => setModalOpen(true), 500);
      }
    } catch {
      // 401 / network failure — silent. Celebration is non-critical UX.
    } finally {
      // Release the in-flight guard immediately; the server gate handles
      // long-term dedup. Modal-open state alone gates further triggers.
      inFlightRef.current = false;
    }
  }, []);

  const value = useMemo(() => ({ triggerAhaCheck }), [triggerAhaCheck]);

  return (
    <AhaCelebrationContext.Provider value={value}>
      {children}
      <AhaCelebrationModal open={modalOpen} onOpenChange={setModalOpen} />
    </AhaCelebrationContext.Provider>
  );
}

export function useAhaCelebration() {
  return useContext(AhaCelebrationContext);
}
